import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps, useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router-dom";

import { ErrorModal } from "./ErrorModal";
import loginStyle from "./login.scss";
import LoginForm, { FormValues, hidePasswordLoginSection, hideSamlLoginSection } from "./LoginForm";
import Export from "components/icons/Export";
import LeftArrow from "components/icons/LeftArrow";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { Logo } from "components/logo/Logo";
import modalStyle from "components/modal/modal.scss";
import { HOME_ROUTE, INTEGRATIONS_ROUTE } from "components/router/Routes";
import Heading from "components/typography/heading/Heading";
import { LoginMethod } from "domain/tenants";
import { UserDetails } from "domain/user";
import { apiGatewayService } from "services/api/ApiGatewayService";
import { authenticationService } from "services/security/AuthenticationService";
import { setDataCollection } from "services/settings/settingsRepository";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { Application } from "store/application";
import { clearAllTenantDetails, pushTenantDetails } from "store/tenantDetails";
import { setUser } from "store/user";
import buttonsStyle from "styles/buttons.scss";
import form from "styles/form.scss";
import { logger } from "utils/logging";
import { getObject, remove, RepositoryKey, setObject } from "utils/repository";
import { storeAndUpdateUserDetails } from "utils/session";

import testIds from "testIds.json";

export const SAFE_RETURN_VALUE = /^\/(?!\/)(?!login\b)[^\\\s]*$/i;

const isSafeReturnTo = (value: string | null): value is string => value !== null && SAFE_RETURN_VALUE.test(value);

const readStoredReturnTo = () =>
    isSafeReturnTo(getObject<string>(RepositoryKey.LOGIN_RETURN_TO))
        ? getObject<string>(RepositoryKey.LOGIN_RETURN_TO)
        : undefined;

const MAP_STATE = (state: StoreState) => ({
    authenticated: state.userReducer.user,
    theme: state.themeReducer.theme,
});
const MAP_DISPATCH = { setUser, pushTenantDetails, clearAllTenantDetails };
const CONNECTOR = connect(MAP_STATE, MAP_DISPATCH);
const USER_DISABLED_MESSAGE = "User disabled";
const ACCOUNT_LOCKOUT_MESSAGE = "Error account lockout";
const UNABLE_TO_FETCH = "Unable to retrieve user data";

enum ViewState {
    SHOW_LOGIN_FORM,
    SHOW_LOADING_INDICATOR,
    REQUIRE_EULA_ACCEPTANCE,
    REDIRECT_TO_APPLICATION,
}

interface LoginDetailsDto {
    login_url?: string;
    login_method?: LoginMethod;
}

export interface LoginDetails {
    loginUrl?: string;
    loginMethod?: LoginMethod;
}

function toLoginDetails(dto: LoginDetailsDto): LoginDetails {
    return {
        loginUrl: dto.login_url,
        loginMethod: dto.login_method,
    };
}

const LoginView = (props: ConnectedProps<typeof CONNECTOR>) => {
    const { t } = useTranslation();
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    // The user might be already authenticated when entering this view, if that is the case then redirects directly
    // to the application. Otherwise goes through the normal login flow.
    const [viewState, setViewState] = React.useState<ViewState>(
        props.authenticated ? ViewState.REDIRECT_TO_APPLICATION : ViewState.SHOW_LOGIN_FORM
    );
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const [scrolledToBottom, setScrollToBottom] = React.useState<boolean>(false);
    const [acceptEula, setAcceptEula] = React.useState<boolean>(false);
    const [userDetails, setUserDetails] = React.useState<UserDetails | null>(null);
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const query = new URLSearchParams(useLocation().search);
    const ssoAuthorizationCode = query.get("code");
    const returnTo = readStoredReturnTo();
    const [samlLoginSectionHidden, toggleSamlLoginSectionHidden] = React.useState<boolean>(true);
    const [forgotPasswordLinkHidden, setForgotPasswordLinkHidden] = React.useState<boolean>(false);
    const handleAuthenticationResponse = (user: UserDetails) => {
        setUserDetails(user);
        setViewState(
            user.eulaAcceptedDate &&
                Date.parse(user.eulaAcceptedDate) >= Date.parse(process.env.EULA_RELEASE_DATE as string)
                ? ViewState.REDIRECT_TO_APPLICATION
                : ViewState.REQUIRE_EULA_ACCEPTANCE
        );
        setDataCollection(user.usageStatistics);
    };

    const handleAuthenticationResponseError = (message: string) => {
        if (message === USER_DISABLED_MESSAGE) {
            setUserDisabledVisible(true);
        } else if (message === UNABLE_TO_FETCH) {
            setGenericErrorVisible(true);
        } else if (message === ACCOUNT_LOCKOUT_MESSAGE) {
            setErrorMessage(t("LoginForm.accountLockoutError"));
        } else {
            setErrorMessage(t("LoginForm.incorrectCredentials"));
        }
    };

    const loginSubmitHandler = (values: FormValues) => {
        setViewState(ViewState.SHOW_LOADING_INDICATOR);
        userSessionService
            .login(values.email, values.password)
            .then((user: UserDetails) => {
                handleAuthenticationResponse(user);
            })
            .catch((e) => {
                let error_message = e;
                try {
                    const response = JSON.parse(e.message);
                    logger.error("Authentication failed", values.email, response.error);
                    error_message = response.error.message;
                } finally {
                    handleAuthenticationResponseError(error_message);
                    setViewState(ViewState.SHOW_LOGIN_FORM);
                }
            });
    };
    const ssoConfigurationCheckHandler = (email: FormValues["email"]) => {
        return apiGatewayService
            .invokeApi("/authentication/sso/check?username=" + encodeURIComponent(email), "GET")
            .then((responseDto: LoginDetailsDto) => {
                const response = toLoginDetails(responseDto);
                setForgotPasswordLinkHidden(hidePasswordLoginSection(response));
                toggleSamlLoginSectionHidden(hideSamlLoginSection(response));
                return response;
            });
    };

    const onAcceptEulaClicked = () => {
        setAcceptEula(!acceptEula);
    };
    const onContinueClicked = () => {
        if (acceptEula) {
            setViewState(ViewState.REDIRECT_TO_APPLICATION);
            // Fire-and-forget style invocation. Doesn't keep the user from logging in even if this call fails.
            apiGatewayService.invokeApi("/eula/accept", "post").then(() => {
                setObject(RepositoryKey.TRIAL_ONBOARDING_POPUP, true);
                userSessionService.fetchUserDetails().then((user: UserDetails) => {
                    storeAndUpdateUserDetails(user, {
                        setUser: props.setUser,
                        clearAllTenantDetails: props.clearAllTenantDetails,
                        pushTenantDetails: props.pushTenantDetails,
                    });
                });
            });
        }
    };

    const handleSsoAuthentication = () => {
        if (ssoAuthorizationCode && ssoAuthorizationCode.trim() !== "") {
            setViewState(ViewState.SHOW_LOADING_INDICATOR);
            userSessionService
                .ssoLogin(ssoAuthorizationCode)
                .then((user: UserDetails) => {
                    handleAuthenticationResponse(user);
                })
                .catch((e) => {
                    logger.error("SSO authentication failed", e.message);
                    try {
                        const response = JSON.parse(e.message);
                        handleAuthenticationResponseError(response.error.message);
                    } catch {
                        setGenericErrorVisible(true);
                    }
                    setViewState(ViewState.SHOW_LOGIN_FORM);
                });
        }
    };

    React.useEffect(() => {
        if (viewState !== ViewState.REDIRECT_TO_APPLICATION) {
            return;
        }
        remove(RepositoryKey.LOGIN_RETURN_TO);
        const oauthState = query.get("oauth_state");
        if (oauthState) {
            authenticationService
                .oauthFinish(oauthState)
                .then((result) => {
                    const redirectUrl = new URL(result.redirectUri);
                    redirectUrl.searchParams.set("code", result.code);
                    if (result.state) {
                        redirectUrl.searchParams.set("state", result.state);
                    }
                    window.location.href = redirectUrl.toString();
                })
                .catch((e) => {
                    logger.error("OAuth finish failed", e.message);
                    setGenericErrorVisible(true);
                    setViewState(ViewState.SHOW_LOGIN_FORM);
                });
        } else if (userDetails) {
            storeAndUpdateUserDetails(userDetails, {
                setUser: props.setUser,
                clearAllTenantDetails: props.clearAllTenantDetails,
                pushTenantDetails: props.pushTenantDetails,
            });
        }
    }, [viewState]);

    React.useEffect(() => {
        handleSsoAuthentication();
    }, []);

    React.useEffect(() => {
        if (viewState !== ViewState.REQUIRE_EULA_ACCEPTANCE && iframeRef.current == null) {
            return;
        }

        const handleScrolling = () => {
            const iframeDocument = iframeRef.current?.contentWindow?.document.documentElement;

            if (
                iframeDocument?.scrollHeight == null ||
                iframeDocument?.scrollTop == null ||
                iframeDocument?.clientHeight == null
            ) {
                return;
            }
            const atBottom = iframeDocument.scrollHeight - iframeDocument.scrollTop - iframeDocument.clientHeight <= 10;

            if (!scrolledToBottom) {
                setScrollToBottom(atBottom);
            }
        };

        iframeRef.current?.contentWindow?.addEventListener("scroll", handleScrolling);

        return () => {
            iframeRef.current?.contentWindow?.removeEventListener("scroll", handleScrolling);
        };
    }, [viewState]);

    const [userDisabledVisible, setUserDisabledVisible] = React.useState<boolean>(false);
    const [genericErrorVisible, setGenericErrorVisible] = React.useState<boolean>(false);
    const [loginSectionVisible, setLoginSectionVisible] = React.useState<boolean>(false);
    const [userEmail, setUserEmail] = React.useState<string>("");

    switch (viewState) {
        case ViewState.SHOW_LOGIN_FORM:
            return (
                <div className={loginStyle.viewContainer}>
                    <div className={loginStyle.formContainer}>
                        <div className={loginStyle.formPanel}>
                            <Logo width={300} height={80} />
                            {loginSectionVisible && (
                                <div
                                    data-testid={testIds.login.form.backToEmailInputButton}
                                    onClick={() => setLoginSectionVisible(!loginSectionVisible)}
                                    className={loginStyle.navigationBackWrapper}
                                >
                                    <div className={classNames(loginStyle.leftArrow, loginStyle.navigationFormatting)}>
                                        <LeftArrow color={props.theme.iconFillColor} />
                                    </div>
                                    <div className={loginStyle.navigationFormatting}>{userEmail && userEmail}</div>
                                </div>
                            )}
                            <Heading tag="h1" className={loginStyle.header}>
                                {t("Common.loginTitle")}
                            </Heading>
                            <LoginForm
                                submitHandler={loginSubmitHandler}
                                error={{
                                    errorMessage,
                                    setErrorMessage,
                                }}
                                ssoConfigurationCheckHandler={ssoConfigurationCheckHandler}
                                login={{
                                    loginSectionVisible,
                                    setLoginSectionVisible,
                                }}
                                email={{
                                    userEmail,
                                    setUserEmail,
                                }}
                                forgotPasswordLinkHidden={forgotPasswordLinkHidden}
                                samlLoginSectionHidden={samlLoginSectionHidden}
                            />
                        </div>
                    </div>
                    <div className={loginStyle.imageContainer}>
                        <div className={loginStyle.downloadMobileAppContainer}>
                            <h1>{t("Login.downloadMobileApp.title")}</h1>
                            <div>
                                <div className={loginStyle.downloadQrCode} />
                                <div className={loginStyle.downloadMobileAppDescriptionAndLinks}>
                                    <div>{t("Login.downloadMobileApp.description")}</div>
                                    <div className={loginStyle.storeLinks}>
                                        <div>
                                            <a
                                                href="https://apps.apple.com/us/app/blancco-management-portal/id1633229601"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <img
                                                    src="https://toolbox.marketingtools.apple.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1663113600&h=35000566bea0a33996078ade9becc199"
                                                    alt="Download on the App Store"
                                                    className={loginStyle.appStoreLink}
                                                />
                                            </a>
                                        </div>
                                        <div>
                                            <a
                                                href="https://play.google.com/store/apps/details?id=com.blancco.android.portal"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <img
                                                    alt="Get it on Google Play"
                                                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                                    className={loginStyle.googlePlayLink}
                                                />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ErrorModal
                        hide={() => setUserDisabledVisible(false)}
                        visible={userDisabledVisible}
                        title={"Login.userDisabledModal.title"}
                        description={"Login.userDisabledModal.description"}
                    />
                    <ErrorModal
                        hide={() => setGenericErrorVisible(false)}
                        visible={genericErrorVisible}
                        title={"Login.failureModal.title"}
                        description={"Login.failureModal.description"}
                    />
                </div>
            );
        case ViewState.SHOW_LOADING_INDICATOR:
            return <LoadingIndicator />;
        case ViewState.REQUIRE_EULA_ACCEPTANCE:
            return (
                <div className={loginStyle.eulaBackgroundImage}>
                    <div className={loginStyle.blanccoLogo}>
                        <Logo isWhiteLogo width={300} height={80} />
                    </div>
                    <div className={classNames(loginStyle.eulaModalWrapper, modalStyle.modalAfterOpen)}>
                        <div>
                            <label className={loginStyle.eulaTextLabelSize}>{t("Login.acceptEulaModal.title")}</label>
                            <label>{t("Login.acceptEulaModal.scrollToContinueInstructionLabel")}</label>
                        </div>
                        <div className={loginStyle.eulaActionsContainer}>
                            <Export color={props.theme.iconFillColor} />
                            <a
                                href="/public/eula/eula.pdf"
                                target="_blank"
                                data-testid={testIds.login.eula.downloadPdfLink}
                            >
                                {t("Login.acceptEulaModal.downloadPdf")}
                            </a>
                        </div>
                        <div className={loginStyle.eulaDivContainer}>
                            <iframe
                                ref={iframeRef}
                                src="/public/eula/eula.html"
                                scrolling="yes"
                                className={loginStyle.eulaContainer}
                                data-testid={testIds.login.eula.contentIframe}
                            />
                        </div>
                        <div className={loginStyle.acceptEulaCheckbox}>
                            <label className={form.container}>
                                <input
                                    type="checkbox"
                                    checked={acceptEula}
                                    className={form.input}
                                    onClick={onAcceptEulaClicked}
                                    data-testid={testIds.login.eula.acceptEulaCheckbox}
                                />
                                <span className={form.checkmark} />
                            </label>
                            <label>{t("Login.acceptEulaModal.agreeLabel")}</label>
                        </div>
                        <div>
                            <button
                                className={buttonsStyle.primaryButtonWithoutIcon}
                                disabled={!acceptEula || !scrolledToBottom}
                                onClick={onContinueClicked}
                                data-testid={testIds.login.eula.continueButton}
                            >
                                {t("Login.acceptEulaModal.continueButton")}
                            </button>
                        </div>
                    </div>
                </div>
            );
        case ViewState.REDIRECT_TO_APPLICATION:
            return (
                <Redirect
                    to={
                        returnTo ??
                        (application === Application.BAM
                            ? (userDetails?.default_settings?.default_view ?? INTEGRATIONS_ROUTE.path)
                            : (userDetails?.default_settings?.default_view ?? HOME_ROUTE.path))
                    }
                />
            );
    }
};

export default CONNECTOR(LoginView);
